/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.store.alerts.impl

import kotlinx.coroutines.flow.map
import kpt.core.base.store.annotation.CacheKey
import kpt.core.base.store.annotation.StoreProvider
import kpt.core.base.store.infra.StoreFactory
import kpt.core.database.alerts.AlertDao
import kpt.core.database.alerts.AlertEntity
import kpt.core.model.alerts.PriceAlert
import org.mobilenativefoundation.store.store5.MutableStore
import org.mobilenativefoundation.store.store5.SourceOfTruth
import org.mobilenativefoundation.store.store5.Store

/**
 * Build an offline-only [Store] for price alerts.
 *
 * Backed exclusively by [AlertDao] — there is no remote fetcher. Alerts are
 * user-defined and stored locally; the repository layer writes them via the
 * DAO's [AlertDao.upsert] / [AlertDao.upsertAll] mutations and the Store
 * surfaces changes reactively.
 *
 * Key: [Unit] — always returns all alerts (no per-alert keyed filter here;
 * per-id lookup goes through the repository directly).
 *
 * The DAO reader is a plain Room `Flow`; Room's InvalidationTracker re-emits to live collectors after writes
 * even when Room 3 alpha05's async InvalidationTracker fails to fan out (see
 * `core-base/database/.../invalidation/README.md`). The paired write notification is
 * emitted by `AlertsRepositoryImpl`'s plain DAO write
 * on `alertDao.upsert` / `deleteById`. On Android/Desktop/iOS the wrap is a microsecond
 * no-op alongside Room's native invalidation.
 */
@StoreProvider(id = "alerts")
@CacheKey(name = "LIST", key = "alerts")
fun provideAlertsStore(dao: AlertDao): Store<Unit, List<PriceAlert>> = StoreFactory.createOfflineStore(
    sourceOfTruth = SourceOfTruth.of(
        // Emit the DOMAIN model — the entity→domain map lives in the SourceOfTruth (read-path contract).
        reader = { _: Unit ->
            dao.observeAll().map { rows -> rows.map(AlertEntity::toPriceAlert) }
        },
        writer = { _: Unit, alerts: List<PriceAlert> -> dao.upsertAll(alerts.map(PriceAlert::toAlertEntity)) },
        delete = { _: Unit -> dao.deleteAll() },
        deleteAll = { dao.deleteAll() },
    ),
)

/**
 * Per-item WRITE store for price alerts (keyed by alert id). Every mutation flows through
 * `store.write` / `store.clear` via [kpt.core.base.store.mutation.MutationGateway], so the
 * repository never touches the DAO — the SoT writer/delete are the single DAO callers. Local-only
 * ([StoreFactory.createOfflineMutableStore] — no-op Updater); the writer/delete fire
 * a plain DAO write so the paired [provideAlertsStore] read collectors re-emit on wasmJs.
 */
@StoreProvider(id = "alertsMutable", logout = false)
fun provideAlertsWriteStore(dao: AlertDao): MutableStore<String, PriceAlert> =
    StoreFactory.createOfflineMutableStore(
        sourceOfTruth = SourceOfTruth.of(
            reader = { id: String ->
                dao.observeById(id).map { it?.toPriceAlert() }
            },
            writer = { _: String, alert: PriceAlert ->
                dao.upsert(alert.toAlertEntity())
            },
            delete = { id: String -> dao.deleteById(id) },
            deleteAll = { dao.deleteAll() },
        ),
    )
